import React, { forwardRef } from "react";
import "./FiltersBar.scss";

// Fontaine M38 — FiltersBar (`ft-filters`). ONE filter row for every list surface: the search
// field, the filter controls, and whatever trailing switch or action the page owns.
//
// It is a LAYOUT and GROUPING primitive and nothing else. That is the whole design:
//
//   · It renders no control of its own. Search is whatever you pass (`SearchInput`), filters are
//     whatever you pass (`Select`, `SortSelect`, `Segmented`), the trailing slot is whatever you
//     pass. Nothing is cloned, wrapped or re-bound, so handlers, `disabled`, `aria-*` and any
//     analytics props on those controls reach the DOM untouched.
//   · It holds no filter state and knows no filter vocabulary. Pages keep owning their own
//     values and their own request shape — a shared bar must never become a place where a page's
//     query semantics quietly live.
//   · One markup at every width. From 900px up it is a single row: search takes the free space,
//     the controls sit beside it, the trailing slot is pushed to the end. Below 900px the three
//     slots stack and the controls wrap. Nothing is duplicated or hidden per width, so there is
//     no second DOM path to keep in sync and no horizontal scrollbar on phone (the charter's
//     "phone never shows a visible scrollbar" is met by wrapping, not by hiding an overflow).
//
// Semantics: with a search slot the bar is a `search` landmark, otherwise a labelled `group` —
// so a screen-reader user can jump to it and hears one name for the whole row rather than a
// loose run of unrelated comboboxes. `label` is therefore effectively required; it is the only
// thing that makes the group a group.
//
// There is no frozen Style Guide V2 specimen for a filter row beyond the charter's `.filter-row`
// sketch (40px controls, 8px gaps, trailing controls pushed right), so this file expresses that
// sketch in tokens and invents no colour, radius, type step or duration of its own.

/**
 * @param {ReactNode} [search]       leading elastic slot — normally a <SearchInput>
 * @param {ReactNode} [children]     the filter controls, in the order they should be read
 * @param {ReactNode} [actions]      trailing slot — a switch, a view toggle, an export button
 * @param {string} label             accessible name for the whole row (e.g. "Filter clients")
 * @param {string} [className]
 */
const FiltersBar = forwardRef(function FiltersBar(
  { search, children, actions, label, className = "", ...rest },
  ref
) {
  const classes = ["ft-filters", className].filter(Boolean).join(" ");

  return (
    <div
      ref={ref}
      className={classes}
      role={search ? "search" : "group"}
      aria-label={label}
      {...rest}
    >
      {search ? <div className="ft-filters__search">{search}</div> : null}
      {children ? <div className="ft-filters__controls">{children}</div> : null}
      {actions ? <div className="ft-filters__actions">{actions}</div> : null}
    </div>
  );
});

export default FiltersBar;
